import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import type {
  DashboardPeriod,
  DashboardPeriodParams,
  PeriodBucket,
  PeriodComparisonMetric,
} from './finance.api'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_DASHBOARD')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export type DashboardOperationsPeriodParams = DashboardPeriodParams

export interface RevenueOverviewSummary {
  collection: PeriodComparisonMetric
  revenue: PeriodComparisonMetric
  netProfit: PeriodComparisonMetric
  expenseRatio: PeriodComparisonMetric
}

export interface RevenueBreakdown {
  patient: number
  insurance: number
}

export interface NetProfitBreakdown {
  gross: number
  net: number
  adjustment: number
  expenses: number
}

export type CollectionPaymentMethod = 'Insurance' | 'Cash' | 'Credit Card'

export type CollectionPostedBy = 'Auto-post' | 'Frontdesk'

export interface CollectionBreakdownRow {
  patientId: string
  patientName: string
  paymentMethod: CollectionPaymentMethod
  amount: number
  datetime: string
  postedBy: CollectionPostedBy
}

export type ProviderRole = 'Dentist' | 'Hygienist'

export interface ProviderRankingRow {
  rank: number
  provider: string
  role: ProviderRole
  production: number
}

export interface CollectionByCategoryRow {
  category: string
  value: number
}

export interface RevenueOverviewResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  summary: RevenueOverviewSummary
  collectionPeriodSummary: PeriodBucket[]
  revenueBreakdown: RevenueBreakdown
  netProfitBreakdown: NetProfitBreakdown
  collectionBreakdown: CollectionBreakdownRow[]
  providerRanking: ProviderRankingRow[]
  collectionByCategory: CollectionByCategoryRow[]
}

export interface InsuranceClaimsOutstandingSummary {
  openClaims: number
  submittedBalance: number
  outstandingBalance: number
  averageDaysOutstanding: number
  pendingPayers: number
  appealsOpen: number
}

export interface InsuranceClaimsPaymentPostingSummary {
  averageDaysToPay: number
  unpostedInsurance: number
  estimatedInsuranceCollection: number
  averageDaysToPost: number
  contractualAdjustments: number
}

export interface InsuranceClaimsPipelineRow {
  appointmentId: string
  patientId: string
  patientName: string
  payer: string
  claimId: string
  dateOfService: string
  procedure: string
  amount: number
  dateSubmitted: string | null
  status: string
}

export interface InsuranceClaimsResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  asOfDate: string
  claimsOutstanding: InsuranceClaimsOutstandingSummary
  paymentPosting: InsuranceClaimsPaymentPostingSummary
  claimsFiledVolume: PeriodBucket[]
  appointmentPipeline: InsuranceClaimsPipelineRow[]
}

export const getRevenueOverview = (
  params: DashboardOperationsPeriodParams,
  options?: { signal?: AbortSignal },
): Promise<RevenueOverviewResult> =>
  axios
    .get<
      ApiResponse<RevenueOverviewResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/operations/revenue-overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getRevenueOverviewOverview = (
  params: DashboardOperationsPeriodParams,
  options?: { signal?: AbortSignal },
): Promise<RevenueOverviewResult> =>
  axios
    .get<
      ApiResponse<RevenueOverviewResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/operations/revenue-overview/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getInsuranceClaims = (
  params: DashboardOperationsPeriodParams,
  options?: { signal?: AbortSignal },
): Promise<InsuranceClaimsResult> =>
  axios
    .get<
      ApiResponse<InsuranceClaimsResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/operations/insurance-claims`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getInsuranceClaimsOverview = (
  params: DashboardOperationsPeriodParams,
  options?: { signal?: AbortSignal },
): Promise<InsuranceClaimsResult> =>
  axios
    .get<
      ApiResponse<InsuranceClaimsResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/operations/insurance-claims/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)
